import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  InputAdornment,
  Tooltip,
  Fade,
  CircularProgress,
  Autocomplete,
  Select,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SellIcon from '@mui/icons-material/Sell';
import FilterListIcon from '@mui/icons-material/FilterList';
import ConstructionIcon from '@mui/icons-material/Construction';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Trade, TradeFilters, Client, TradeType } from '../types';
import { tradeService } from '../services/trade.service';
import { clientService } from '../services/client.service';
import { marketService } from '../services/market.service';
import { useAuth } from '../contexts/AuthContext';
import { useReadOnlyAction } from '../hooks/useReadOnlyAction';
import { TRADE_STATUS } from '../utils/constants';
import { TradeDialog } from '../components/common/TradeDialog';
import { calculateChargesBreakdown, DEFAULT_OPTION_BROKERAGE } from '../hooks/useTradeCalculations';
import PageHeader from '../components/common/PageHeader';

// ============================================================================
// CONSTANTS & COLORS (matching TradingDashboard)
// ============================================================================

const COLORS = {
  primary: '#0A0A0A',
  primaryLight: '#525252',
  primaryDark: '#262626',
  positive: '#10B981',
  positiveLight: '#D1FAE5',
  negativeDark: '#DC2626',
  negative: '#EF4444',
  negativeLight: '#FEE2E2',
  warning: '#F59E0B',
  blue: '#3B82F6',
  pageBg: '#F8F7FF',
  cardBg: '#FFFFFF',
  textPrimary: '#1E1B4B',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
};

const formatCurrency = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);

const formatCurrencyFull = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);

// Show the full 7-decimal precision for itemised charges so STT/stamp/GST
// sub-paise values are visible alongside stored backend values.
const formatChargesPrecise = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 7, maximumFractionDigits: 7 }).format(val);

const formatPercent = (val: number): string =>
  `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;

// Helper: is equity/intraday type
function isEquityType(tt: string): boolean {
  return tt === TradeType.EQUITY || tt === TradeType.STOCK || tt === TradeType.INTRA_STOCK;
}

function isFutureType(tt: string): boolean {
  return tt === 'Future';
}

// ============================================================================
// DATE NAVIGATION HELPERS (same pattern as Analytics / DealerAnalytics)
// ============================================================================

type DateMode = 'day' | 'week' | 'month' | 'year' | 'all';
type SortDirection = 'asc' | 'desc';
interface SortConfig { key: string; direction: SortDirection; }

function startOfDay(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function addDays(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
function isSameDay(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function weekStart(d: Date) { return addDays(startOfDay(d), -d.getDay()); }
function weekEnd(d: Date) { return addDays(weekStart(d), 6); }
function monthStart(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function monthEnd(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function yearStart(d: Date) { return new Date(d.getFullYear(), 0, 1); }
function yearEnd(d: Date) { return new Date(d.getFullYear(), 11, 31); }

function getDateRange(mode: DateMode, sel: Date): { start: Date; end: Date } | null {
  if (mode === 'all') return null;
  if (mode === 'day') { const s = startOfDay(sel); return { start: s, end: new Date(s.getTime() + 86400000 - 1) }; }
  if (mode === 'week') return { start: weekStart(sel), end: weekEnd(sel) };
  if (mode === 'month') return { start: monthStart(sel), end: monthEnd(sel) };
  return { start: yearStart(sel), end: yearEnd(sel) };
}

function dateRangeLabel(mode: DateMode, sel: Date): string {
  if (mode === 'all') return 'All Time';
  if (mode === 'day') return sel.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (mode === 'week') return `${fmt(weekStart(sel))} – ${fmt(weekEnd(sel))}`;
  if (mode === 'month') return sel.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  return `${sel.getFullYear()}`;
}

function bumpDate(mode: DateMode, sel: Date, dir: -1 | 1): Date {
  if (mode === 'day') return addDays(sel, dir);
  if (mode === 'week') return addDays(sel, 7 * dir);
  if (mode === 'month') return new Date(sel.getFullYear(), sel.getMonth() + dir, 1);
  if (mode === 'year') return new Date(sel.getFullYear() + dir, 0, 1);
  return sel;
}

function isNextDisabled(mode: DateMode, sel: Date): boolean {
  if (mode === 'all') return true;
  const rng = getDateRange(mode, sel);
  return rng ? rng.end >= new Date() : true;
}


function isTradeInPeriod(tradeDate: string, start: Date, end: Date): boolean {
  const d = new Date(tradeDate);
  return d >= start && d <= end;
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

// Matches dashboard open positions columns + Buy/Sell/Status for full view
const TABLE_COLUMNS = [
  { k: 'serialNumber', l: '#', w: 40 },
  { k: 'clientName', l: 'Client', w: 90 },
  { k: 'scrip', l: 'Scrip', w: 120 },
  { k: 'direction', l: 'B/S', w: 35 },
  { k: 'tradeType', l: 'Type', w: 60 },
  { k: 'createdAt', l: 'Date', w: 85 },
  { k: 'balanceLots', l: 'Lots', w: 55, a: 'right' as const },
  { k: '_shares', l: 'Shares', w: 65, a: 'right' as const },
  { k: 'purchasePrice', l: 'Buy', w: 80, a: 'right' as const },
  { k: 'currentPrice', l: 'CMP', w: 80, a: 'right' as const },
  { k: 'investedAmount', l: 'Invested', w: 95, a: 'right' as const },
  { k: 'currentValue', l: 'Curr Val', w: 95, a: 'right' as const },
  { k: 'unrealizedPnL', l: 'Unreal P&L', w: 95, a: 'right' as const },
  { k: 'realizedPnL', l: 'Real P&L', w: 90, a: 'right' as const },
  { k: 'totalPnL', l: 'Total P&L', w: 95, a: 'right' as const },
  { k: 'pnlPercentage', l: '%', w: 70, a: 'right' as const },
  { k: 'status', l: 'Status', w: 75 },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TradesList() {
  const { isAdmin, canModify, isSuperAdmin } = useAuth();
  const { isReadOnly, snackbarOpen, closeSnackbar, message, showWorkInProgress } = useReadOnlyAction('trades');
  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<TradeFilters>(() => {
    const initial: TradeFilters = {};
    const status = searchParams.get('status');
    if (status) initial.status = status as TradeFilters['status'];
    const clientId = searchParams.get('clientId');
    if (clientId) initial.clientId = clientId;
    const tradeType = searchParams.get('tradeType');
    if (tradeType) initial.tradeType = tradeType as TradeFilters['tradeType'];
    return initial;
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState<string | null>(null);
  const [wipeAllOpen, setWipeAllOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const canDelete = isAdmin || isSuperAdmin;

  const handleWipeAll = async () => {
    setWiping(true);
    try {
      const r = await tradeService.deleteAllTrades();
      setTrades([]);
      setSuccess(`Deleted ${r.trades} trades (all import data cleared)`);
      setWipeAllOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to delete all trades');
    } finally {
      setWiping(false);
    }
  };

  // TradeDialog state
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [editingTrade, setEditingTrade] = useState<Trade | null>(null);

  // Trade detail popup state (view-only)
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);

  // Date navigation (replaces old period filter + start/end date pickers)
  const [dateMode, setDateMode] = useState<DateMode>(() => {
    const p = searchParams.get('period');
    return p ? (p as DateMode) : 'day';
  });
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Sort
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Admin always sees action buttons, but in read-only mode they show "Work in Progress"
  const showActionButtons = canModify || isAdmin;

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      // Delivery/Intraday are client-side aliases for Equity subtypes — don't
      // forward them to the backend. Future/Option map directly to DB values.
      const apiFilters = { ...filters };
      const tt = apiFilters.tradeType as unknown as string;
      if (tt === 'Delivery' || tt === 'Intraday') {
        delete apiFilters.tradeType;
      }
      const [tradesData, clientsData] = await Promise.all([
        tradeService.getTrades(apiFilters),
        clientService.getClients(),
      ]);
      setTrades(tradesData);
      setClients(clientsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  // Live price refresh for open positions (same as TradingDashboard)
  const refreshPrices = useCallback(async () => {
    const scrips = [...new Set(trades.filter((t) => t.status === 'OPEN' && !t.isDeleted).map((t) => t.scrip).filter(Boolean))];
    if (scrips.length === 0) return;
    try {
      const { prices } = await marketService.getQuotes(scrips);
      setTrades((prev) =>
        prev.map((t) => {
          if (t.status === 'OPEN' && prices[t.scrip]) {
            const livePrice = prices[t.scrip].price;
            if (!livePrice || livePrice <= 0) return t;
            const balanceLots = t.purchaseLots - (t.saleLots || 0);
            const investedAmount = balanceLots * t.lotSize * t.purchasePrice;
            const currentValue = livePrice * balanceLots * t.lotSize;
            const isSellFirst = t.direction === 'SELL';
            const unrealizedPnL = isSellFirst ? (investedAmount - currentValue) : (currentValue - investedAmount);
            const totalPnL = (t.realizedPnL || 0) + unrealizedPnL;
            // Recompute % from the live total so it can never disagree with the
            // displayed Total P&L. Denominator matches the backend:
            // purchaseLots × lotSize × purchasePrice.
            const totalInvestment = t.purchaseLots * t.lotSize * t.purchasePrice;
            const pnlPercentage = totalInvestment > 0 ? (totalPnL / totalInvestment) * 100 : 0;
            return { ...t, currentPrice: livePrice, currentValue, unrealizedPnL, totalPnL, pnlPercentage };
          }
          return t;
        })
      );
    } catch { /* non-fatal */ }
  }, [trades]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const istHour = (now.getUTCHours() + 5) % 24 + (now.getUTCMinutes() + 30 >= 60 ? 1 : 0);
      const istMin = (now.getUTCMinutes() + 30) % 60;
      const day = now.getUTCDay();
      if (day >= 1 && day <= 5 && (istHour > 9 || (istHour === 9 && istMin >= 0)) && (istHour < 15 || (istHour === 15 && istMin <= 35))) {
        refreshPrices();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshPrices]);

  // Handle filter change
  const handleFilterChange = (field: keyof TradeFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setPage(0);
  };

  // Handle New Trade button
  const handleNavigateToNewTrade = () => {
    if (isReadOnly) {
      showWorkInProgress();
      return;
    }
    setEditingTrade(null);
    setShowTradeDialog(true);
  };

  // Handle edit
  const handleEditTrade = async (tradeId: string) => {
    if (isReadOnly) {
      showWorkInProgress();
      return;
    }
    try {
      const trade = await tradeService.getTradeById(tradeId);
      setEditingTrade(trade);
      setShowTradeDialog(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load trade for editing');
    }
  };

  // Handle delete dialog open with read-only check
  const handleOpenDeleteDialog = (tradeId: string) => {
    if (isReadOnly) {
      showWorkInProgress();
      return;
    }
    setTradeToDelete(tradeId);
    setDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!tradeToDelete) return;

    try {
      await tradeService.deleteTrade(tradeToDelete);
      setTrades((prev) => prev.filter((t) => t.tradeId !== tradeToDelete));
      setSuccess('Trade deleted successfully');
      setDeleteDialogOpen(false);
      setTradeToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete trade');
    }
  };

  // TradeDialog callbacks
  const handleTradeDialogClose = () => {
    setShowTradeDialog(false);
    setEditingTrade(null);
  };

  const handleTradeDialogSuccess = () => {
    setShowTradeDialog(false);
    setEditingTrade(null);
    setSuccess(editingTrade ? 'Trade updated successfully' : 'Trade created successfully');
    fetchData();
  };

  // Sort handler
  const handleSort = (key: string) =>
    setSortConfig((p) => ({ key, direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc' }));

  // Date range for filtering
  const dateRange = useMemo(() => getDateRange(dateMode, selectedDate), [dateMode, selectedDate]);

  // Filtered + sorted trades
  const filteredTrades = useMemo(() => {
    let list = trades.filter((t) => !t.isDeleted);

    // Trade type filter
    const filterTT = filters.tradeType as unknown as string;
    if (filterTT === 'Delivery') {
      list = list.filter((t) => t.tradeType === TradeType.EQUITY || t.tradeType === TradeType.STOCK);
    } else if (filterTT === 'Intraday') {
      list = list.filter((t) => t.tradeType === TradeType.INTRA_STOCK);
    } else if (filterTT === 'Future') {
      list = list.filter((t) => t.tradeType === TradeType.FUTURE);
    } else if (filterTT === 'Option') {
      list = list.filter((t) => t.tradeType === TradeType.OPTION);
    }

    // Date range filter (closed → saleDate, open → buy date)
    if (dateRange) {
      list = list.filter((t) => {
        const bucketDate = t.status === 'CLOSED'
          ? (t.saleDate || t.date || t.createdAt)
          : (t.date || t.createdAt);
        return isTradeInPeriod(bucketDate, dateRange.start, dateRange.end);
      });
    }

    return list;
  }, [trades, dateRange, filters.tradeType]);

  const sortedTrades = useMemo(() => {
    const s = [...filteredTrades];
    s.sort((a, b) => {
      let av: any;
      let bv: any;
      if (sortConfig.key === '_shares') {
        av = (a.purchaseLots - (a.saleLots || 0)) * (a.lotSize || 1);
        bv = (b.purchaseLots - (b.saleLots || 0)) * (b.lotSize || 1);
      } else if (sortConfig.key === 'createdAt') {
        // For closed trades, sort by saleDate; for open trades, sort by createdAt
        av = a.status === 'CLOSED' ? (a.saleDate || a.createdAt) : a.createdAt;
        bv = b.status === 'CLOSED' ? (b.saleDate || b.createdAt) : b.createdAt;
      } else {
        av = (a as any)[sortConfig.key] ?? 0;
        bv = (b as any)[sortConfig.key] ?? 0;
      }
      if (typeof av === 'string') return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortConfig.direction === 'asc' ? av - bv : bv - av;
    });
    return s;
  }, [filteredTrades, sortConfig]);

  // Paginated slice
  const paginatedTrades = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedTrades.slice(start, start + rowsPerPage);
  }, [sortedTrades, page, rowsPerPage]);

  // Active clients
  const activeClients = useMemo(() => clients.filter((c) => c.isActive), [clients]);

  // Actions column def (only if showActionButtons)
  const hasActions = showActionButtons;

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', pb: 3 }}>
      {/* Header */}
      <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2 }}>
        <PageHeader
          eyebrow="Trading"
          title="Trades"
          subtitle={
            isReadOnly
              ? 'View and manage all trades (Read-only mode)'
              : 'View and manage all trades'
          }
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setWipeAllOpen(true)}
                  disabled={trades.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  Delete all
                </Button>
              )}
              {showActionButtons && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNavigateToNewTrade}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    ...(isReadOnly
                      ? { opacity: 0.8 }
                      : { bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark } }),
                  }}
                >
                  New Trade
                </Button>
              )}
            </Box>
          }
        />
      </Box>

      {/* Alerts */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Read-only mode snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="info"
          icon={<ConstructionIcon />}
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: '1px solid #F59E0B',
            color: '#92400E',
            '& .MuiAlert-icon': { color: '#D97706' },
          }}
        >
          {message}
        </Alert>
      </Snackbar>

      <Box sx={{ px: { xs: 1.5, md: 2.5 } }}>
        {/* Date Navigation Toolbar */}
        <Paper
          elevation={0}
          sx={{ mb: 2, p: 1.25, borderRadius: 2, bgcolor: '#FFFFFF', border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}
        >
          <Box sx={{ display: 'inline-flex', p: 0.375, bgcolor: '#F1F5F9', borderRadius: 1.5, gap: 0.25 }}>
            {(['day', 'week', 'month', 'year', 'all'] as DateMode[]).map((m) => {
              const active = dateMode === m;
              const label = m === 'day' ? 'Day' : m === 'week' ? 'Week' : m === 'month' ? 'Month' : m === 'year' ? 'Year' : 'All';
              return (
                <Box key={m} onClick={() => { setDateMode(m); if (m !== 'day') setSelectedDate(new Date()); setPage(0); }}
                  sx={{ px: 1.5, py: 0.625, borderRadius: 1, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', color: active ? '#0F172A' : '#64748B', bgcolor: active ? '#FFFFFF' : 'transparent', boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none', transition: 'all 0.15s ease', '&:hover': { color: '#0F172A' } }}>
                  {label}
                </Box>
              );
            })}
          </Box>
          {dateMode !== 'all' && (
            <>
              <Box sx={{ width: '1px', height: 24, bgcolor: '#E2E8F0' }} />
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton size="small" onClick={() => { setSelectedDate((d) => bumpDate(dateMode, d, -1)); setPage(0); }}
                  sx={{ width: 30, height: 30, border: '1px solid #E2E8F0', borderRadius: 1, color: '#64748B', '&:hover': { bgcolor: '#F8FAFC', color: '#0F172A' } }}>
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box sx={{ px: 1.5, py: 0.75, minWidth: 200, textAlign: 'center', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1, fontSize: '0.8rem', fontWeight: 600, color: '#0F172A', fontFeatureSettings: '"tnum"' }}>
                  {dateRangeLabel(dateMode, selectedDate)}
                </Box>
                <IconButton size="small" onClick={() => { setSelectedDate((d) => bumpDate(dateMode, d, 1)); setPage(0); }} disabled={isNextDisabled(dateMode, selectedDate)}
                  sx={{ width: 30, height: 30, border: '1px solid #E2E8F0', borderRadius: 1, color: '#64748B', '&:hover': { bgcolor: '#F8FAFC', color: '#0F172A' }, '&.Mui-disabled': { opacity: 0.4 } }}>
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {!isSameDay(selectedDate, new Date()) && dateMode === 'day' && (
                  <Button size="small" onClick={() => { setSelectedDate(new Date()); setPage(0); }}
                    sx={{ ml: 0.5, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', color: COLORS.primary, px: 1.25, minWidth: 'auto', '&:hover': { bgcolor: '#EEF2FF' } }}>
                    Today
                  </Button>
                )}
              </Box>
            </>
          )}
          {dateMode === 'all' && <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748B' }}>All-time data</Typography>}
          <Box sx={{ flex: 1 }} />
          <Chip label={`${filteredTrades.length} trades`} size="small" sx={{ bgcolor: `${COLORS.primary}15`, color: COLORS.primary, fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
        </Paper>

        {/* Filter Row */}
        <Paper elevation={0} sx={{ mb: 2, p: 1.25, borderRadius: 2, bgcolor: '#FFFFFF', border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          {/* Client — searchable autocomplete */}
          <Autocomplete
            size="small"
            options={activeClients}
            getOptionLabel={(c) => c.name}
            value={activeClients.find((c) => c.clientId === filters.clientId) || null}
            onChange={(_, val) => handleFilterChange('clientId', val?.clientId || '')}
            renderInput={(params) => (
              <TextField {...params} placeholder="All Clients" InputProps={{ ...params.InputProps, startAdornment: <>{params.InputProps.startAdornment}<InputAdornment position="start"><FilterListIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment></> }} />
            )}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', bgcolor: '#F8FAFC' } }}
            disableClearable={!filters.clientId}
            forcePopupIcon={false}
          />

          {/* Status */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              displayEmpty
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              sx={{ borderRadius: 1.5, fontSize: '0.82rem', bgcolor: '#F8FAFC', color: filters.status ? '#0F172A' : '#94A3B8', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' } }}
            >
              <MenuItem value="">Status</MenuItem>
              {TRADE_STATUS.filter((s) => s.value).map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Trade Type */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              displayEmpty
              value={(filters.tradeType as string) || ''}
              onChange={(e) => handleFilterChange('tradeType', e.target.value)}
              sx={{ borderRadius: 1.5, fontSize: '0.82rem', bgcolor: '#F8FAFC', color: (filters.tradeType as string) ? '#0F172A' : '#94A3B8', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' } }}
            >
              <MenuItem value="">Trade Type</MenuItem>
              <MenuItem value="Delivery">Delivery</MenuItem>
              <MenuItem value="Intraday">Intraday</MenuItem>
              <MenuItem value="Future">Future</MenuItem>
              <MenuItem value="Option">Option</MenuItem>
            </Select>
          </FormControl>

          {/* Scrip search */}
          <TextField
            size="small" placeholder="Search scrip…" value={filters.scrip || ''} onChange={(e) => handleFilterChange('scrip', e.target.value)}
            sx={{ minWidth: 140, flex: '0 1 200px', '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', bgcolor: '#F8FAFC' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> }}
          />
        </Paper>

        {/* Trades Table */}
        <Fade in>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {TABLE_COLUMNS.map((col) => (
                      <TableCell
                        key={col.k}
                        align={col.a || 'left'}
                        sx={{
                          bgcolor: '#F9FAFB',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          color: COLORS.textSecondary,
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          borderBottom: `2px solid ${COLORS.border}`,
                          py: 1,
                          px: 1,
                          width: col.w,
                        }}
                      >
                        <TableSortLabel
                          active={sortConfig.key === col.k}
                          direction={sortConfig.key === col.k ? sortConfig.direction : 'desc'}
                          onClick={() => handleSort(col.k)}
                        >
                          {col.l}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    {hasActions && (
                      <TableCell
                        sx={{
                          bgcolor: '#F9FAFB',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          color: COLORS.textSecondary,
                          textTransform: 'uppercase',
                          borderBottom: `2px solid ${COLORS.border}`,
                          py: 1,
                          px: 1,
                          width: 100,
                        }}
                      >
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMNS.length + (hasActions ? 1 : 0)} align="center" sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={28} sx={{ color: '#667eea' }} />
                          <Typography variant="body2" color="text.secondary">Loading trades…</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : paginatedTrades.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMNS.length + (hasActions ? 1 : 0)} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">No trades found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTrades.map((t, idx) => {
                      const eq = isEquityType(t.tradeType);
                      const isClosed = t.status === 'CLOSED';
                      const balLots = t.purchaseLots - (t.saleLots || 0);
                      const totalInvestment = t.purchaseLots * (t.lotSize || 1) * t.purchasePrice;
                      const invested = isClosed ? totalInvestment : balLots * (t.lotSize || 1) * t.purchasePrice;
                      const saleProceeds = (t.saleLots || 0) * (t.lotSize || 1) * (t.salePrice || 0);
                      const currVal = balLots > 0 ? (t.currentPrice || t.purchasePrice) * balLots * (t.lotSize || 1) : 0;
                      // Use backend-computed P&L (includes charges)
                      const realPnL = t.realizedPnL || 0;
                      const unrealPnL = t.unrealizedPnL || 0;
                      const totalPnL = t.totalPnL || (realPnL + unrealPnL);
                      // Always derive % from the displayed total (never the stored value) so
          // the % can't disagree with Total P&L after a live-price refresh.
          const pnlPct = totalInvestment > 0 ? (totalPnL / totalInvestment) * 100 : 0;
                      const prof = totalPnL >= 0;

                      return (
                        <TableRow
                          key={t.tradeId}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: `${COLORS.primary}05` },
                            borderLeft: `3px solid ${prof ? COLORS.positive : COLORS.negative}`,
                            opacity: isClosed ? 0.75 : 1,
                          }}
                          onClick={() => setSelectedTrade(t)}
                        >
                          {/* # */}
                          <TableCell sx={{ fontWeight: 600, color: COLORS.textSecondary, fontSize: '0.75rem', px: 1 }}>
                            {t.serialNumber || page * rowsPerPage + idx + 1}
                          </TableCell>
                          {/* Client */}
                          <TableCell sx={{ px: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '0.75rem' }}>{t.clientName}</Typography>
                          </TableCell>
                          {/* Scrip */}
                          <TableCell sx={{ px: 1 }}>
                            <Chip label={t.scrip} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: `${COLORS.primary}10`, color: COLORS.primary, height: 22 }} />
                          </TableCell>
                          {/* Direction */}
                          <TableCell sx={{ px: 0.5 }}>
                            <Chip
                              label={(t.direction || 'BUY') === 'SELL' ? 'S' : 'B'}
                              size="small"
                              sx={{
                                fontSize: '0.6rem', height: 18, fontWeight: 700, minWidth: 22,
                                bgcolor: (t.direction || 'BUY') === 'SELL' ? '#FEE2E2' : '#D1FAE5',
                                color: (t.direction || 'BUY') === 'SELL' ? '#DC2626' : '#059669',
                              }}
                            />
                          </TableCell>
                          {/* Type */}
                          <TableCell sx={{ px: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap' }}>
                              <Chip
                                label={eq ? 'Eq' : isFutureType(t.tradeType) ? 'Fut' : 'Opt'}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 20, fontWeight: 600 }}
                              />
                              {/* Equity sub-label based on buy date vs today (IST).
                                  Bought today → Intraday (could still close same-day).
                                  Bought before today → Delivery (carried forward).
                                  INTRA_STOCK (already reclassified) → always Intraday. */}
                              {/* Intraday if INTRA_STOCK in DB OR buy date = today (IST).
                                  Delivery if buy date is before today. Always uses t.date. */}
                              {eq && (() => {
                                if (t.tradeType === TradeType.INTRA_STOCK) return <Chip label="Intraday" size="small" sx={{ fontSize: '0.55rem', height: 18, fontWeight: 700, bgcolor: '#FCE7F3', color: '#BE185D', letterSpacing: '0.02em' }} />;
                                const d = new Date(t.date || '');
                                if (isNaN(d.getTime())) return null;
                                const buyDay = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                                const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                                return buyDay === today
                                  ? <Chip label="Intraday" size="small" sx={{ fontSize: '0.55rem', height: 18, fontWeight: 700, bgcolor: '#FCE7F3', color: '#BE185D', letterSpacing: '0.02em' }} />
                                  : <Chip label="Delivery" size="small" sx={{ fontSize: '0.55rem', height: 18, fontWeight: 700, bgcolor: '#DBEAFE', color: '#1D4ED8', letterSpacing: '0.02em' }} />;
                              })()}
                              {/* Option: Call vs Put */}
                              {t.tradeType === TradeType.OPTION && (t.scrip.toUpperCase().endsWith('CE') || t.scrip.toUpperCase().endsWith('PE')) && (
                                <Chip
                                  label={t.scrip.toUpperCase().endsWith('CE') ? 'Call' : 'Put'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.55rem', height: 18, fontWeight: 700,
                                    bgcolor: t.scrip.toUpperCase().endsWith('CE') ? '#DCFCE7' : '#FEE2E2',
                                    color: t.scrip.toUpperCase().endsWith('CE') ? '#047857' : '#B91C1C',
                                    letterSpacing: '0.02em',
                                  }}
                                />
                              )}
                              {t.isMTF && (
                                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7', px: 0.5, py: 0.1, borderRadius: 0.5, lineHeight: 1.2 }}>
                                  MTF{t.leverage ? ` ${t.leverage}` : ''}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          {/* Date — closed trades show sale date, open trades show buy date */}
                          <TableCell sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, fontWeight: 600, whiteSpace: 'nowrap', px: 1 }}>
                            {(() => {
                              const dateStr = isClosed ? (t.saleDate || t.date || t.createdAt) : (t.date || t.createdAt);
                              if (!dateStr) return '-';
                              return (
                                <>
                                  {new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                  <Typography component="span" sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, ml: 0.5 }}>
                                    {new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                  </Typography>
                                </>
                              );
                            })()}
                          </TableCell>
                          {/* Lots — same as dashboard: show balance lots, dash for equity */}
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1 }}>
                            {eq ? '\u2014' : (isClosed ? `${t.purchaseLots}` : `${balLots}`)}
                          </TableCell>
                          {/* Shares — lots × lotSize */}
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1 }}>
                            {isClosed ? t.purchaseLots * (t.lotSize || 1) : balLots * (t.lotSize || 1)}
                          </TableCell>
                          {/* Buy */}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', px: 1 }}>{formatCurrencyFull(t.purchasePrice)}</TableCell>
                          {/* CMP */}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, px: 1 }}>
                            {isClosed ? formatCurrencyFull(t.salePrice || 0) : formatCurrencyFull(t.currentPrice || t.purchasePrice)}
                          </TableCell>
                          {/* Invested */}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', px: 1 }}>{formatCurrency(invested)}</TableCell>
                          {/* Curr Val */}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, px: 1 }}>
                            {isClosed ? formatCurrency(saleProceeds) : formatCurrency(currVal)}
                          </TableCell>
                          {/* Unreal P&L */}
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: unrealPnL >= 0 ? COLORS.positive : COLORS.negative, px: 1 }}>
                            {unrealPnL !== 0 ? formatCurrency(unrealPnL) : '\u2014'}
                          </TableCell>
                          {/* Real P&L */}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: realPnL >= 0 ? COLORS.positive : COLORS.negative, px: 1, fontWeight: realPnL !== 0 ? 600 : 400 }}>
                            {realPnL !== 0 ? formatCurrency(realPnL) : '\u2014'}
                          </TableCell>
                          {/* Total P&L */}
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: prof ? COLORS.positive : COLORS.negative, px: 1 }}>
                            {formatCurrency(totalPnL)}
                          </TableCell>
                          {/* % */}
                          <TableCell align="right" sx={{ px: 1 }}>
                            <Chip
                              label={formatPercent(pnlPct)}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.6rem',
                                bgcolor: prof ? COLORS.positiveLight : COLORS.negativeLight,
                                color: prof ? COLORS.positive : COLORS.negativeDark,
                                height: 20,
                              }}
                            />
                          </TableCell>
                          {/* Status */}
                          <TableCell sx={{ px: 1 }}>
                            <Chip
                              label={t.status || '-'}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.6rem',
                                height: 20,
                                bgcolor: t.status === 'OPEN' ? `${COLORS.positive}15` : t.status === 'CLOSED' ? `${COLORS.textSecondary}15` : `${COLORS.warning}15`,
                                color: t.status === 'OPEN' ? COLORS.positive : t.status === 'CLOSED' ? COLORS.textSecondary : COLORS.warning,
                              }}
                            />
                          </TableCell>
                          {/* Actions */}
                          {hasActions && (
                            <TableCell sx={{ px: 1 }} onClick={(e) => e.stopPropagation()}>
                              <Box display="flex" gap={0.3}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditTrade(t.tradeId)}
                                    sx={{ color: COLORS.primary, ...(isReadOnly ? { opacity: 0.7 } : {}) }}
                                  >
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                {t.balanceLots > 0 && (
                                  <Tooltip title="Add Sale">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        if (isReadOnly) {
                                          showWorkInProgress();
                                        } else {
                                          handleEditTrade(t.tradeId);
                                        }
                                      }}
                                      sx={{ color: COLORS.blue, ...(isReadOnly ? { opacity: 0.7 } : {}) }}
                                    >
                                      <SellIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {(isAdmin || isSuperAdmin) && (
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDeleteDialog(t.tradeId)}
                                      sx={{ color: COLORS.negative, ...(isReadOnly ? { opacity: 0.7 } : {}) }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedTrades.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{
                borderTop: `1px solid ${COLORS.border}`,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem',
                  color: COLORS.textSecondary,
                  fontWeight: 600,
                },
              }}
            />
          </Paper>
        </Fade>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <Box sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
              bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <DeleteIcon sx={{ fontSize: 28, color: '#DC2626' }} />
          </Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', mb: 0.5 }}>
            Delete this trade?
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B', px: 3, lineHeight: 1.5 }}>
            This trade will be permanently removed. This action cannot be undone.
          </Typography>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
              borderColor: '#E2E8F0', color: '#475569', borderRadius: 1.5, px: 3,
              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
              bgcolor: '#DC2626', borderRadius: 1.5, px: 3,
              boxShadow: '0 2px 6px rgba(220, 38, 38, 0.25)',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.35)' },
            }}
          >
            Delete Trade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete ALL trades — admin/super-admin only */}
      <Dialog open={wipeAllOpen} onClose={() => !wiping && setWipeAllOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <Box sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2, bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteIcon sx={{ fontSize: 28, color: '#DC2626' }} />
          </Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', mb: 0.5 }}>
            Delete ALL trades?
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B', px: 3, lineHeight: 1.5 }}>
            This permanently removes <b>every trade</b> ({trades.length}) and all imported trade data across the firm, and cannot be undone. Client capital is preserved.
          </Typography>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, justifyContent: 'center', gap: 1 }}>
          <Button onClick={() => setWipeAllOpen(false)} disabled={wiping} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', borderColor: '#E2E8F0', color: '#475569', borderRadius: 1.5, px: 3, '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' } }}>
            Cancel
          </Button>
          <Button onClick={handleWipeAll} disabled={wiping} variant="contained" startIcon={wiping ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', bgcolor: '#DC2626', borderRadius: 1.5, px: 3, boxShadow: 'none', '&:hover': { bgcolor: '#B91C1C' } }}>
            {wiping ? 'Deleting…' : 'Delete all'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trade Dialog */}
      <TradeDialog
        key={editingTrade ? editingTrade.tradeId : 'new'}
        open={showTradeDialog}
        onClose={handleTradeDialogClose}
        onSuccess={handleTradeDialogSuccess}
        clients={clients}
        editTrade={editingTrade}
      />

      {/* Trade Detail Popup (view-only) */}
      <Dialog open={!!selectedTrade} onClose={() => setSelectedTrade(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {selectedTrade && (() => {
          const t = selectedTrade;
          const cl = clients.find(c => c.clientId === t.clientId);
          const eq = isEquityType(t.tradeType);
          const isOpt = t.tradeType === TradeType.OPTION;
          const isFut = t.tradeType === TradeType.FUTURE;
          const isClosed = t.status === 'CLOSED';
          const dir = t.direction || 'BUY';
          const isSF = dir === 'SELL';
          const balLots = t.purchaseLots - (t.saleLots || 0);
          const lotSize = t.lotSize || 1;
          const shares = t.purchaseLots * lotSize;
          const balShares = balLots * lotSize;
          const totalInvestment = t.purchaseLots * lotSize * t.purchasePrice;
          const saleValue = (t.saleLots || 0) * lotSize * (t.salePrice || 0);
          const exch = t.exchange || 'NSE';

          // Intraday vs Delivery: INTRA_STOCK in DB OR buy date = today (IST).
          const isIntra = (() => {
            if (t.tradeType === TradeType.INTRA_STOCK) return true;
            if (!eq) return false;
            const d = new Date(t.date || '');
            if (isNaN(d.getTime())) return false;
            const buyDay = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
            const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
            return buyDay === today;
          })();
          const displayType = isOpt ? 'Option' : isFut ? 'Future' : isIntra ? 'Intraday' : 'Delivery';

          // ── Single source of truth: backend-computed values ──
          // P&L, brokerage, and percentages come from the backend response.
          // The backend's calculateTradeMetrics handles direction, trade type,
          // and brokerage rates consistently. We do NOT recompute here.
          const realPnL = t.realizedPnL || 0;
          const unrealPnL = t.unrealizedPnL || 0;
          const totalPnL = t.totalPnL || (realPnL + unrealPnL);
          // Always derive % from the displayed total (never the stored value) so
          // the % can't disagree with Total P&L after a live-price refresh.
          const pnlPct = totalInvestment > 0 ? (totalPnL / totalInvestment) * 100 : 0;
          // ── Itemised charges breakdown (display only) ──
          // The backend stores only the total brokerageAmount. The line-item
          // split (STT, exchange, stamp, GST) is deterministic display math
          // derived from the same inputs — kept on the frontend because it's
          // purely presentation and doesn't feed back into any calculation.
          const brokerageRate = isOpt ? 0 : isFut ? (cl?.brokerageRates?.future || 0.0003) : eq ? (isIntra ? (cl?.brokerageRates?.intraStock || 0.0003) : (cl?.brokerageRates?.stock || 0.003)) : 0.003;
          const optPerLot = isOpt ? (cl?.brokerageRates?.option || DEFAULT_OPTION_BROKERAGE) : 0;

          const buyVal = t.purchaseLots * lotSize * t.purchasePrice;
          const buyFixedBrk = isOpt ? optPerLot * t.purchaseLots : undefined;
          const buyCharges = calculateChargesBreakdown(t.tradeType, buyVal, 0, brokerageRate, isIntra, exch, buyFixedBrk);

          let sellCharges: any = null;
          if (t.saleLots > 0 && t.salePrice > 0) {
            const sellFixedBrk = isOpt ? optPerLot * t.saleLots : undefined;
            sellCharges = calculateChargesBreakdown(t.tradeType, 0, saleValue, brokerageRate, isIntra, exch, sellFixedBrk);
          }
          const totalCharges = buyCharges.totalCharges + (sellCharges?.totalCharges || 0);

          const profColor = (v: number) => v >= 0 ? COLORS.positive : COLORS.negative;
          // Pin to IST — trades are executed in Indian market hours regardless
          // of where the viewer's browser is running. Without `timeZone` the
          // displayed hour shifts when viewed outside IST.
          const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';
          const fmtTime = (d: string) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }) : '';

          return (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: COLORS.primary, color: '#fff', py: 1.5, px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>Trade Details</Typography>
                  <Chip label={t.scrip} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', height: 22 }} />
                  <Chip label={displayType} size="small" sx={{ fontWeight: 600, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', height: 20, fontSize: '0.65rem' }} />
                  {isOpt && (
                    <Chip label={t.scrip.toUpperCase().endsWith('CE') ? 'Call' : 'Put'} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', bgcolor: t.scrip.toUpperCase().endsWith('CE') ? '#DBEAFE' : '#FCE7F3', color: t.scrip.toUpperCase().endsWith('CE') ? '#1D4ED8' : '#BE185D' }} />
                  )}
                  <Chip label={dir} size="small" sx={{ fontWeight: 700, bgcolor: dir === 'BUY' ? '#22C55E' : '#EF4444', color: '#fff', height: 20, fontSize: '0.65rem' }} />
                  <Chip label={isClosed ? 'CLOSED' : 'OPEN'} size="small" sx={{ fontWeight: 700, bgcolor: isClosed ? '#6B7280' : '#3B82F6', color: '#fff', height: 20, fontSize: '0.65rem' }} />
                </Box>
                <IconButton size="small" onClick={() => setSelectedTrade(null)} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                {/* Trade Info Grid */}
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, bgcolor: '#F9FAFB', borderBottom: `1px solid ${COLORS.border}` }}>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Client</Typography><Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{t.clientName}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Sell' : 'Buy'} Date</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{fmtDate(t.date)} {fmtTime(t.date)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Exchange</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{exch}</Typography></Box>
                </Box>

                {/* Quantities + Prices */}
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, borderBottom: `1px solid ${COLORS.border}` }}>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Sell' : 'Buy'} Lots</Typography><Typography sx={{ fontWeight: 700 }}>{t.purchaseLots}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Lot Size</Typography><Typography sx={{ fontWeight: 700 }}>{lotSize}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Shares</Typography><Typography sx={{ fontWeight: 700 }}>{shares}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Sell' : 'Buy'} Price</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrencyFull(t.purchasePrice)}</Typography></Box>
                </Box>

                {/* Sale Info (if sold) */}
                {t.saleLots > 0 && (
                  <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, borderBottom: `1px solid ${COLORS.border}`, bgcolor: '#FAFAFA' }}>
                    <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Cover' : 'Sell'} Lots</Typography><Typography sx={{ fontWeight: 700 }}>{t.saleLots}</Typography></Box>
                    <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Cover' : 'Sell'} Price</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrencyFull(t.salePrice)}</Typography></Box>
                    <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Cover' : 'Sale'} Date</Typography><Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{fmtDate(t.saleDate || '')} {fmtTime(t.saleDate || '')}</Typography></Box>
                    <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Balance</Typography><Typography sx={{ fontWeight: 700 }}>{balLots} lots ({balShares} sh)</Typography></Box>
                  </Box>
                )}

                {/* P&L Summary */}
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, borderBottom: `1px solid ${COLORS.border}` }}>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{isSF ? 'Sell Value' : 'Invested'}</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrency(totalInvestment)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Current Value</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrency(t.currentValue || 0)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>CMP</Typography><Typography sx={{ fontWeight: 700 }}>{formatCurrencyFull(t.currentPrice || t.purchasePrice)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Total P&L</Typography><Typography sx={{ fontWeight: 800, color: profColor(totalPnL) }}>{formatCurrency(totalPnL)} ({pnlPct.toFixed(2)}%)</Typography></Box>
                </Box>

                {/* P&L Breakdown */}
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, borderBottom: `1px solid ${COLORS.border}`, bgcolor: '#FAFAFA' }}>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Realized P&L</Typography><Typography sx={{ fontWeight: 700, color: profColor(realPnL) }}>{formatCurrency(realPnL)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Unrealized P&L</Typography><Typography sx={{ fontWeight: 700, color: profColor(unrealPnL) }}>{formatCurrency(unrealPnL)}</Typography></Box>
                </Box>

                {/* Charges Breakdown */}
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 1, color: COLORS.textPrimary }}>Charges Breakdown</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 0.5 }}>Charge</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 0.5 }}>Buy Side</TableCell>
                        {sellCharges && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 0.5 }}>Sell Side</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 0.5 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        { label: isOpt ? `Brokerage (₹${optPerLot}/lot)` : `Brokerage (${(brokerageRate * 100).toFixed(2).replace(/\.?0+$/, '')}%)`, key: 'brokerage' },
                        { label: 'STT', key: 'stt' },
                        { label: `Exchange (${exch})`, key: 'exchangeCharges' },
                        { label: 'SEBI', key: 'sebiCharges' },
                        { label: 'Stamp Duty', key: 'stampDuty' },
                        { label: 'GST (18%)', key: 'gst' },
                      ].map(row => (
                        <TableRow key={row.key}>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5 }}>{formatChargesPrecise((buyCharges as any)[row.key])}</TableCell>
                          {sellCharges && <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5 }}>{formatChargesPrecise((sellCharges as any)[row.key])}</TableCell>}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, py: 0.5 }}>{formatChargesPrecise((buyCharges as any)[row.key] + ((sellCharges as any)?.[row.key] || 0))}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#F0F0FF' }}>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.8rem', py: 0.5 }}>Total Charges</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.5 }}>{formatChargesPrecise(buyCharges.totalCharges)}</TableCell>
                        {sellCharges && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.5 }}>{formatChargesPrecise(sellCharges.totalCharges)}</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.85rem', color: COLORS.warning, py: 0.5 }}>{formatChargesPrecise(totalCharges)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>

                {/* MTF / Deployed Info */}
                {(t.isMTF || Number(t.deployedAmount) > 0) && (
                  <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {t.isMTF && <Chip label={`MTF${t.leverage ? ` ${t.leverage}` : ''}`} size="small" sx={{ fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }} />}
                    {Number(t.deployedAmount) > 0 && <Chip label={`Deployed: ${formatCurrency(t.deployedAmount || 0)}`} size="small" variant="outlined" sx={{ fontWeight: 600, borderColor: '#7C3AED', color: '#7C3AED' }} />}
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 2, pb: 1.5, justifyContent: 'space-between' }}>
                <Button onClick={() => setSelectedTrade(null)} sx={{ textTransform: 'none', color: COLORS.textSecondary }}>Close</Button>
                {!isReadOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => { setSelectedTrade(null); setEditingTrade(t); setShowTradeDialog(true); }}
                    sx={{ textTransform: 'none', bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark } }}
                  >
                    Edit Trade
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}

export default TradesList;
